import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingScreen from '../common/LoadingScreen';

const ProtectedRoute = ({ 
  children, 
  requireAdmin = false,
  requirePageManager = false,
  requireAccounting = false,
  requireQuickBooks = false,
  requireDocSign = false,
  requireApplications = false,
  requireWaterUsage = false,
  requireInsurance = false,
  requireCameras = false,
  requireTraffic = false,
  requireDropbox = false,
  requireDoors = false,
  requireFuel = false,
  requirePortalDocs = false,
  requireDriverAnalytics = false,
  requireDriverTablets = false,
  requireTabletTab = null,
  requiredRole = null,
  fallbackPath = '/login' 
}) => {
  const { user, loading, isAuthenticated, isAdmin, canManagePages, canAccessAccounting, canAccessQuickBooks, canAccessDocSign, canAccessApplications, canAccessWaterUsage, canAccessInsurance, canAccessCameras, canAccessTraffic, canAccessDropbox, canAccessDoors, canAccessFuel, canManagePortalDocs, canAccessDriverAnalytics, canAccessDriverTablets, canSeeTabletTab } = useAuth();

  // Show loading while checking authentication
  if (loading) {
    return <LoadingScreen message="Verifying access..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check page manager requirement (admin or canManagePages)
  if (requirePageManager && !canManagePages()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check accounting access requirement (admin or canAccessAccounting)
  if (requireAccounting && !canAccessAccounting()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check QuickBooks access requirement (admin or canAccessQuickBooks)
  if (requireQuickBooks && !canAccessQuickBooks()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check DocSign access requirement (admin or canAccessDocSign)
  if (requireDocSign && !canAccessDocSign()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Applications access requirement (admin or canAccessApplications)
  if (requireApplications && !canAccessApplications()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Water Usage access requirement (admin or canAccessWaterUsage)
  if (requireWaterUsage && !canAccessWaterUsage()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Insurance access requirement (admin or canAccessInsurance)
  if (requireInsurance && !canAccessInsurance()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Cameras access requirement (admin or canAccessCameras)
  if (requireCameras && !canAccessCameras()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Traffic access requirement (admin or canAccessTraffic)
  if (requireTraffic && !canAccessTraffic()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Dropbox access requirement (admin or canAccessDropbox)
  if (requireDropbox && !canAccessDropbox()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Fuel access requirement (admin or canAccessFuel)
  if (requireFuel && !canAccessFuel()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Doors access requirement (admin or canAccessDoors)
  if (requireDoors && !canAccessDoors()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Driver docs requirement (admin or canManagePortalDocs)
  if (requirePortalDocs && !canManagePortalDocs()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Driver Analytics access requirement (admin or canAccessDriverAnalytics)
  if (requireDriverAnalytics && !canAccessDriverAnalytics()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Driver Tablets access requirement (admin or canAccessDriverTablets)
  if (requireDriverTablets && !canAccessDriverTablets()) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check Driver Tablets sub-tab permission (admin or tabletTabs includes key)
  if (requireTabletTab && !canSeeTabletTab(requireTabletTab)) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check specific role requirement
  if (requiredRole && user?.role !== requiredRole && !isAdmin) {
    return <Navigate to="/forbidden" replace />;
  }

  // Check if user account is active
  if (user && !user.isActive) {
    return <Navigate to="/forbidden" replace />;
  }

  return children;
};

export default ProtectedRoute; 